import { useState, useEffect, useRef, useCallback } from "react";
import { Network } from "vis-network";
import { DataSet } from "vis-data";
import {
  Settings,
  GitBranch,
  Circle,
  Grid3X3,
  Download,
  X,
  ZoomIn,
  ZoomOut,
  Maximize2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import axios from "axios";

const API = "/api/cyber";

const ENTITY_COLORS = {
  DOMAIN: "#4A90D9",
  IP_ADDRESS: "#F5A623",
  PERSON: "#7ED321",
  ORGANIZATION: "#D0021B",
  EMAIL: "#9B59B6",
  PHONE: "#FF6B6B",
  HASH: "#34495E",
  CRYPTO: "#F39C12",
  URL: "#1ABC9C",
  SOCIAL_MEDIA: "#E91E63",
  CUSTOM: "#95A5A6",
};

const LAYOUTS = [
  {
    id: "hierarchical",
    label: "Hierarchical",
    icon: GitBranch,
    desc: "Trace dependencies - best for command chains",
  },
  {
    id: "organic",
    label: "Organic",
    icon: Settings,
    desc: "Explore dense networks - detect clusters",
  },
  {
    id: "circular",
    label: "Circular",
    icon: Circle,
    desc: "Highlight central actors - identify key influencers",
  },
  {
    id: "orthogonal",
    label: "Orthogonal",
    icon: Grid3X3,
    desc: "Structured data - grid-like view",
  },
];

export default function GraphCanvas({ nodes = [], edges = [], caseId = "" }) {
  const containerRef = useRef(null);
  const networkRef = useRef(null);
  const [currentLayout, setCurrentLayout] = useState("organic");
  const [selectedNode, setSelectedNode] = useState(null);
  const [availableTransforms, setAvailableTransforms] = useState([]);
  const [nodeDetail, setNodeDetail] = useState(null);

  const getLayoutOptions = useCallback((layoutType) => {
    const baseOptions = {
      nodes: {
        shape: "dot",
        size: 25,
        font: { size: 12, face: "Arial" },
        borderWidth: 2,
        shadow: true,
      },
      edges: {
        width: 2,
        smooth: { type: "continuous", roundness: 0.5 },
        arrows: { to: { enabled: true, scaleFactor: 0.5 } },
        font: { size: 10, align: "middle" },
      },
      physics: {
        enabled: true,
        stabilization: { iterations: 100 },
      },
      interaction: {
        hover: true,
        tooltipDelay: 200,
        zoomView: true,
        dragView: true,
      },
    };

    const layouts = {
      hierarchical: {
        layout: {
          hierarchical: {
            enabled: true,
            direction: "LR",
            sortMethod: "directed",
            levelSeparation: 150,
            nodeSpacing: 100,
          },
        },
      },
      organic: {
        layout: { improvedLayout: true, clusterThreshold: 100 },
        physics: {
          barnesHut: {
            gravitationalConstant: -8000,
            centralGravity: 0.3,
            springLength: 95,
            springConstant: 0.04,
            damping: 0.09,
          },
        },
      },
      circular: {
        layout: { randomSeed: 2 },
        physics: false,
      },
      orthogonal: {
        layout: {
          hierarchical: {
            enabled: true,
            direction: "UD",
            sortMethod: "directed",
            levelSeparation: 150,
          },
        },
        edges: { smooth: false },
      },
    };

    return { ...baseOptions, ...(layouts[layoutType] || layouts.organic) };
  }, []);

  const loadTransforms = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/transforms`);
      setAvailableTransforms(res.data.transforms || []);
    } catch {
      // Silently fail if API is unavailable
    }
  }, []);

  const expandNode = useCallback(
    async (nodeId) => {
      const node = nodes.find((n) => n.id === nodeId);
      if (!node) return;
      try {
        await axios.post(`${API}/transforms/expand`, {
          entity_id: nodeId,
          case_id: caseId,
          entity_value: node.value,
          entity_type: node.entity_type,
        });
      } catch {
        // Silently fail if API is unavailable
      }
    },
    [nodes, caseId]
  );

  const changeLayout = useCallback(
    (layoutType) => {
      setCurrentLayout(layoutType);
      if (networkRef.current) {
        networkRef.current.setOptions(getLayoutOptions(layoutType));
        networkRef.current.fit();
      }
    },
    [getLayoutOptions]
  );

  const exportGraph = useCallback(() => {
    const canvas = containerRef.current?.querySelector("canvas");
    if (canvas) {
      const link = document.createElement("a");
      link.download = `cyber-graph-${caseId || "export"}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    }
  }, [caseId]);

  const zoomIn = useCallback(() => {
    networkRef.current?.zoomIn();
  }, []);

  const zoomOut = useCallback(() => {
    networkRef.current?.zoomOut();
  }, []);

  const fitGraph = useCallback(() => {
    networkRef.current?.fit();
  }, []);

  // Initialize vis-network
  useEffect(() => {
    if (!containerRef.current) return;

    const visNodes = new DataSet(
      nodes.map((n) => ({
        id: n.id,
        label: n.label || n.value,
        title: `${n.entity_type || n.type}: ${n.value || n.label}`,
        color: {
          background: ENTITY_COLORS[n.entity_type] || "#95A5A6",
          border: "#ffffff",
        },
        borderWidth: 2,
        size: Math.min(40, 15 + (n.confidence || 50) / 5),
        font: { color: "#e0e0e0", size: 12, face: "Arial" },
        shape: "dot",
      }))
    );

    const visEdges = new DataSet(
      edges.map((e) => ({
        id: e.id,
        from: e.source_entity_id,
        to: e.target_entity_id,
        label: e.relationship_type || e.label,
        color: { color: "#4a5568", opacity: 0.7 },
        width: 1.5 + (e.weight || 0) * 2,
        smooth: { type: "continuous", roundness: 0.5 },
        arrows: { to: { enabled: true, scaleFactor: 0.5 } },
        font: { size: 10, color: "#a0aec0", align: "middle" },
      }))
    );

    const options = getLayoutOptions(currentLayout);

    const network = new Network(
      containerRef.current,
      { nodes: visNodes, edges: visEdges },
      options
    );
    networkRef.current = network;

    network.on("click", (params) => {
      if (params.nodes.length > 0) {
        const nodeId = params.nodes[0];
        const nodeData = nodes.find((n) => n.id === nodeId);
        if (nodeData) {
          setSelectedNode(nodeId);
          setNodeDetail(nodeData);
        }
      } else {
        setSelectedNode(null);
        setNodeDetail(null);
      }
    });

    network.on("doubleClick", (params) => {
      if (params.nodes.length > 0) {
        expandNode(params.nodes[0]);
      }
    });

    network.fit();

    loadTransforms();

    return () => {
      network.destroy();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Update graph when nodes/edges change
  useEffect(() => {
    if (!networkRef.current) return;

    const visNodes = new DataSet(
      nodes.map((n) => ({
        id: n.id,
        label: n.label || n.value,
        title: `${n.entity_type || n.type}: ${n.value || n.label}`,
        color: {
          background: ENTITY_COLORS[n.entity_type] || "#95A5A6",
          border: "#ffffff",
        },
        borderWidth: 2,
        size: Math.min(40, 15 + (n.confidence || 50) / 5),
        font: { color: "#e0e0e0", size: 12, face: "Arial" },
        shape: "dot",
      }))
    );

    const visEdges = new DataSet(
      edges.map((e) => ({
        id: e.id,
        from: e.source_entity_id,
        to: e.target_entity_id,
        label: e.relationship_type || e.label,
        color: { color: "#4a5568", opacity: 0.7 },
        width: 1.5 + (e.weight || 0) * 2,
        smooth: { type: "continuous", roundness: 0.5 },
        arrows: { to: { enabled: true, scaleFactor: 0.5 } },
        font: { size: 10, color: "#a0aec0", align: "middle" },
      }))
    );

    networkRef.current.setData({ nodes: visNodes, edges: visEdges });
    networkRef.current.fit();
  }, [nodes, edges]);

  const hasEmptyGraph = nodes.length === 0;

  return (
    <div
      className="relative w-full rounded-lg overflow-hidden"
      style={{ minHeight: "600px", background: "#1a1a2e" }}
    >
      {/* Graph Canvas */}
      <div ref={containerRef} className="w-full" style={{ height: "600px" }} />

      {/* Layout Controls */}
      <div className="absolute top-3 right-3 flex gap-1.5 z-10">
        {LAYOUTS.map((l) => (
          <button
            key={l.id}
            onClick={() => changeLayout(l.id)}
            title={l.desc}
            className={`px-2.5 py-1.5 rounded text-xs transition-all ${
              currentLayout === l.id
                ? "bg-blue-500 text-white"
                : "bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-600"
            }`}
          >
            {l.label}
          </button>
        ))}
      </div>

      {/* Zoom Controls */}
      <div className="absolute bottom-3 left-3 flex gap-1 z-10">
        <button
          onClick={zoomIn}
          className="p-1.5 bg-gray-800 border border-gray-600 rounded text-gray-300 hover:bg-gray-700"
          title="Zoom in"
        >
          <ZoomIn className="h-4 w-4" />
        </button>
        <button
          onClick={zoomOut}
          className="p-1.5 bg-gray-800 border border-gray-600 rounded text-gray-300 hover:bg-gray-700"
          title="Zoom out"
        >
          <ZoomOut className="h-4 w-4" />
        </button>
        <button
          onClick={fitGraph}
          className="p-1.5 bg-gray-800 border border-gray-600 rounded text-gray-300 hover:bg-gray-700"
          title="Fit"
        >
          <Maximize2 className="h-4 w-4" />
        </button>
        <button
          onClick={exportGraph}
          className="p-1.5 bg-gray-800 border border-gray-600 rounded text-gray-300 hover:bg-gray-700"
          title="Export PNG"
        >
          <Download className="h-4 w-4" />
        </button>
      </div>

      {/* Legend */}
      <div className="absolute bottom-3 right-3 bg-gray-800/90 border border-gray-600 rounded-lg p-3 z-10">
        <h4 className="text-xs font-semibold text-gray-300 mb-1.5">Legend</h4>
        <div className="grid grid-cols-2 gap-x-3 gap-y-1">
          {Object.entries(ENTITY_COLORS).map(([type, color]) => (
            <div key={type} className="flex items-center gap-1.5">
              <div
                className="w-2.5 h-2.5 rounded-full"
                style={{ backgroundColor: color }}
              />
              <span className="text-xs text-gray-400">
                {type.replace(/_/g, " ")}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Node Detail Panel */}
      {nodeDetail && (
        <div className="absolute top-3 left-3 w-72 bg-gray-800/95 border border-gray-600 rounded-lg p-4 z-10 shadow-xl">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-white truncate">
              {nodeDetail.label || nodeDetail.value}
            </h3>
            <button
              onClick={() => {
                setSelectedNode(null);
                setNodeDetail(null);
              }}
              className="text-gray-400 hover:text-white"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <Badge
            className="mb-2"
            style={{
              backgroundColor: ENTITY_COLORS[nodeDetail.entity_type] || "#95A5A6",
            }}
          >
            {nodeDetail.entity_type}
          </Badge>
          <div className="text-xs text-gray-400 space-y-1 mb-3">
            <div>
              <span className="text-gray-500">Value:</span>{" "}
              <span className="text-gray-300 font-mono">
                {nodeDetail.value}
              </span>
            </div>
            <div>
              <span className="text-gray-500">Confidence:</span>{" "}
              <span className="text-gray-300">
                {nodeDetail.confidence || "N/A"}%
              </span>
            </div>
            <div>
              <span className="text-gray-500">Source:</span>{" "}
              <span className="text-gray-300">
                {nodeDetail.source || "Unknown"}
              </span>
            </div>
          </div>

          {availableTransforms.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-gray-400 mb-1.5">
                Transforms
              </h4>
              <div className="flex flex-wrap gap-1">
                {availableTransforms
                  .filter(
                    (t) =>
                      t.input_type === nodeDetail.entity_type || !t.input_type
                  )
                  .slice(0, 5)
                  .map((t) => (
                    <button
                      key={t.id}
                      onClick={() => expandNode(nodeDetail.id)}
                      className="px-2 py-1 bg-gray-700 hover:bg-blue-600 text-gray-300 hover:text-white rounded text-xs transition-colors"
                    >
                      {t.name}
                    </button>
                  ))}
              </div>
            </div>
          )}

          {nodeDetail.properties &&
            Object.keys(nodeDetail.properties).length > 0 && (
              <div className="mt-2 pt-2 border-t border-gray-700">
                <h4 className="text-xs font-semibold text-gray-400 mb-1">
                  Properties
                </h4>
                <div className="text-xs text-gray-400 max-h-24 overflow-y-auto">
                  {Object.entries(nodeDetail.properties)
                    .slice(0, 8)
                    .map(([k, v]) => (
                      <div key={k}>
                        <span className="text-gray-500">{k}:</span>{" "}
                        {typeof v === "object"
                          ? JSON.stringify(v).slice(0, 40)
                          : String(v)}
                      </div>
                    ))}
                </div>
              </div>
            )}
        </div>
      )}

      {hasEmptyGraph && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="text-center text-gray-500">
            <Settings className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p className="font-medium">No graph data</p>
            <p className="text-sm">
              Add entities and relationships to visualize the investigation
              graph.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
